import { Platform, StyleSheet } from 'react-native'
import type { Theme } from '../../../theme/getTheme'

export function getAccountBillingTabStyles(theme: Theme) {
  return StyleSheet.create({
    /** Matches marketing pricing cards / org admin cards — clear boundary on Plan & billing. */
    cardWrap: {
      alignSelf: 'stretch',
      gap: 20,
      paddingVertical: theme.spacing.lg,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: theme.borderRadius.lg,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
    },
    container: {
      gap: 20,
    },

    planSummary: {
      gap: 4,
    },
    planLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    planValue: {
      fontSize: 17,
      fontWeight: '700',
      color: theme.gray[900],
    },
    orgPaidAccessSummary: {
      fontSize: 14,
      fontWeight: '500',
      color: theme.gray[600],
      marginTop: 4,
    },
    orgBillingDisclosure: {
      fontSize: 13,
      lineHeight: 19,
      color: theme.gray[500],
    },

    billingSubsection: {
      gap: 10,
      marginTop: 4,
    },
    subsectionTitle: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[600],
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },

    statusSection: {
      flexDirection: 'row',
      gap: 32,
      flexWrap: 'wrap',
    },
    statusItem: {
      gap: 4,
    },
    statusLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    statusValue: {
      fontSize: 15,
      fontWeight: '500',
      color: theme.gray[800],
    },

    unconfiguredBanner: {
      backgroundColor: theme.gray[100],
      borderRadius: 10,
      padding: 16,
      alignItems: 'center',
    },
    unconfiguredText: {
      fontSize: 14,
      color: theme.gray[600],
      textAlign: 'center',
    },

    actionsRow: {
      flexDirection: 'row',
      gap: 12,
      flexWrap: 'wrap',
      paddingTop: 4,
    },

    portalBtn: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
      alignItems: 'center',
      backgroundColor: theme.background.cardElevated,
    },
    portalBtnPressed: {
      opacity: 0.8,
    },
    portalBtnHover: {
      opacity: 0.92,
    },
    portalBtnDisabled: {
      opacity: 0.45,
    },
    portalBtnText: {
      /** `theme.black` flips: dark copy on light cards, near-white on dark slate (see billing portal spec). */
      color: theme.black,
      fontSize: 15,
      fontWeight: '600',
    },

    comparePlansLink: {
      alignSelf: 'flex-start',
      justifyContent: 'center',
      minHeight: theme.touchTargets.minHeight,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: 0,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
    },
    comparePlansLinkPressed: {
      opacity: 0.75,
    },
    comparePlansLinkHover: {
      opacity: 0.9,
    },
    comparePlansLinkDisabled: {
      opacity: 0.45,
    },
    comparePlansLinkText: {
      color: theme.primary,
      fontSize: 15,
      fontWeight: '600',
    },

    /** One-time Business plan — in-app upgrade section. */
    oneTimePlanSection: {
      gap: theme.spacing.lg,
      paddingTop: theme.spacing.md,
      marginTop: theme.spacing.xs,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200],
    },
    /** Extra air under “Seat tier” before current tier + controls. */
    oneTimeSectionTitleSpacing: {
      marginBottom: theme.spacing.sm,
    },
    seatTierCurrentBlock: {
      gap: theme.spacing.sm,
    },
    /** Divider + padding between “current tier” readout and picker / upgrade. */
    seatTierUpgradeSection: {
      paddingTop: theme.spacing.xxl,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[300],
      gap: theme.spacing.md,
    },
    bucketRow: {
      flexDirection: 'row',
      gap: theme.spacing.lg,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    bucketPickerWrap: {
      flex: 2,
      minWidth: 0,
    },
    bucketPickerWithLoader: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    bucketLoaderSlot: {
      width: 28,
      minHeight: theme.touchTargets.minHeight,
      alignItems: 'center',
      justifyContent: 'center',
    },
    bucketPickerInner: {
      flex: 1,
      minWidth: 0,
      minHeight: theme.touchTargets.minHeight,
      justifyContent: 'center',
    },
    /** FREE state plan picker */
    planPickerHeading: {
      fontSize: 18,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
    },
    planPickerSubheading: {
      fontSize: 15,
      color: theme.gray[500],
      marginBottom: theme.spacing.xl,
    },
    planCard: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      gap: theme.spacing.lg,
      backgroundColor: theme.background.cardElevated,
    },
    planCardHighlighted: {
      borderColor: theme.primaryLight,
      borderWidth: 2,
    },
    planCardName: {
      fontSize: 17,
      fontWeight: '800',
      color: theme.gray[900],
    },
    planCardPrice: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.primaryLight,
    },
    planCardTagline: {
      fontSize: 14,
      color: theme.gray[500],
      lineHeight: 20,
    },
    planCardFeatureList: {
      gap: 8,
    },
    planCardFeatureRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    planCardFeatureText: {
      fontSize: 13,
      color: theme.gray[600],
      flex: 1,
    },
    planCardCta: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      backgroundColor: theme.primary,
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
    },
    planCardCtaPressed: {
      opacity: 0.82,
    },
    planCardCtaHover: {
      opacity: 0.9,
    },
    planCardCtaDisabled: {
      opacity: 0.45,
    },
    planCardCtaText: {
      color: '#fff',
      fontSize: 15,
      fontWeight: '700',
    },
    planCardCtaOutline: {
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderWidth: 1.5,
      borderColor: theme.primaryLight,
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
    },
    planCardCtaOutlineText: {
      color: theme.primaryLight,
      fontSize: 15,
      fontWeight: '600',
    },
    customFormContainer: {
      gap: theme.spacing.md,
      paddingTop: theme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200],
    },
    customFormLabel: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[600],
      marginBottom: 4,
    },
    customFormInput: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: theme.borderRadius.sm,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 15,
      color: theme.gray[900],
      backgroundColor: theme.background.card,
    },
    customFormTextArea: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: theme.borderRadius.sm,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 15,
      color: theme.gray[900],
      backgroundColor: theme.background.card,
      minHeight: 80,
    },
    customFormError: {
      fontSize: 13,
      color: theme.error,
    },
    customFormSuccess: {
      fontSize: 14,
      color: theme.primary,
      fontWeight: '600',
      textAlign: 'center',
    },
    planCardSpacer: {
      height: theme.spacing.xs,
    },

    /** MANUAL / STRIPE_CUSTOM — read-only info panel */
    manualBillingCard: {
      gap: theme.spacing.lg,
    },
    manualPendingBadge: {
      alignSelf: 'flex-start',
      backgroundColor: theme.gray[100],
      borderRadius: 8,
      paddingVertical: 6,
      paddingHorizontal: 12,
    },
    manualPendingText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.gray[500],
    },
    manualSummaryRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 28,
    },
    customEnterpriseHeader: {
      gap: 6,
    },
    customEnterpriseBadge: {
      alignSelf: 'flex-start' as const,
      backgroundColor: `${theme.primary}18`,
      borderRadius: 20,
      paddingHorizontal: 10,
      paddingVertical: 4,
      marginBottom: 4,
    },
    customEnterpriseBadgeText: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.primary,
      letterSpacing: 0.4,
      textTransform: 'uppercase' as const,
    },
    customEnterpriseProduct: {
      fontSize: 20,
      fontWeight: '800',
      color: theme.gray[900],
      letterSpacing: -0.3,
    },
    customEnterprisePrice: {
      fontSize: 26,
      fontWeight: '800',
      color: theme.primaryLight,
      letterSpacing: -0.5,
    },
    customEnterprisePricePlaceholder: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.gray[400],
    },
    customEnterpriseSeats: {
      fontSize: 14,
      color: theme.gray[500],
      fontWeight: '500',
    },

    stripeCustomActions: {
      gap: 10,
      paddingTop: 16,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200],
    },
    stripeCustomPayLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
    },
    stripeCustomPayBtn: {
      paddingVertical: 15,
      paddingHorizontal: 20,
      backgroundColor: theme.primary,
      borderRadius: 14,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      minHeight: 52,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 4px 18px ${theme.primary}55`,
      }),
    },
    stripeCustomPayBtnPressed: {
      opacity: 0.82,
    },
    stripeCustomPayBtnHover: {
      opacity: 0.92,
    },
    stripeCustomPayBtnDisabled: {
      opacity: 0.45,
    },
    stripeCustomPayBtnText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '700',
      letterSpacing: 0.3,
    },
    manualContactLink: {
      alignSelf: 'center',
      justifyContent: 'center',
      minHeight: theme.touchTargets.minHeight,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: 0,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
    },
    manualContactLinkPressed: {
      opacity: 0.75,
    },
    manualContactLinkHover: {
      opacity: 0.9,
    },
    manualContactLinkText: {
      color: theme.primary,
      fontSize: 15,
      fontWeight: '600',
    },
    /** Mirrors the marketing "Back to site" chip — same row height as SeatBucketPicker trigger. */
    upgradeBtn: {
      flex: 1,
      minWidth: 0,
      minHeight: theme.touchTargets.minHeight,
      paddingHorizontal: 16,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.primary,
      borderWidth: 1.5,
      borderColor: theme.primaryLight,
      shadowColor: theme.primary,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    upgradeBtnDisabled: {
      opacity: 0.4,
    },
    upgradeBtnPressed: {
      opacity: 0.85,
    },
    upgradeBtnHover: {
      borderColor: theme.primaryLight,
    },
    upgradeBtnText: {
      color: theme.primaryLight,
      fontSize: 15,
      fontWeight: '600',
    },

  })
}
